// src/pages/Services/reservationService.ts
// Public write-only service used by the "Enquire / Reserve This Car" and
// "Book a Test Drive" forms on the client site. No payment gateway involved —
// this simply captures the lead; staff follow up and record any deposit
// manually from the Admin > Reservations & Sales screen.
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../Firebase/Config';

export type InquiryType = 'Purchase / Reservation' | 'Test Drive Request' | 'General Inquiry';

export interface ReservationSubmission {
  inquiryType: InquiryType;
  // Optional — a test-drive/general inquiry doesn't have to be about one
  // specific car (e.g. "I'd like to test drive any SUV you have").
  carId?: string;
  carName?: string;
  carImage?: string;
  carPrice?: number;
  // Set when the customer picked a brand/model without narrowing down to one
  // specific vehicle — e.g. "I'd like to test drive a Toyota Corolla", not
  // tied to one VIN.
  brandId?: string;
  brandName?: string;
  modelId?: string;
  modelName?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  preferredDate?: string;
  preferredTime?: string;
  message?: string;
  // Set when the customer is signed in at submission time — lets "My
  // Account" show this request in their activity history.
  submittedByUid?: string;
}

export const submitReservationInquiry = async (data: ReservationSubmission): Promise<string> => {
  const ref = await addDoc(collection(db, 'reservations'), {
    ...data,
    status: 'New Inquiry',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

export interface ReservationRecord extends ReservationSubmission {
  id: string;
  status: string;
  createdAt?: Timestamp;
}

// Used by "My Account" to show a signed-in customer's own past
// reservation/test-drive requests (only ones submitted while signed in are
// tagged with submittedByUid, so older anonymous submissions won't appear).
export const fetchMyReservations = async (uid: string): Promise<ReservationRecord[]> => {
  const q = query(collection(db, 'reservations'), where('submittedByUid', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as ReservationSubmission & { status: string; createdAt?: Timestamp }) }));
};
